/**
 * FieldRegistry — F12.A T12.A7.
 *
 * Singleton factory dla 20 typów pól MVP (D2). Każdy typ rejestruje się przez
 * `FieldRegistry.register({ type, renderInput, renderCell, validate, defaultValue })`.
 *
 * Użycie:
 *   import { FieldRegistry } from '@/components/fields/FieldRegistry';
 *   import '@/components/fields/types'; // side-effect: rejestracja 20 typów
 *
 *   FieldRegistry.renderInput({ field, value, onChange });
 *   FieldRegistry.renderCell(field, value);
 *
 * @see notatki/sessions/sesja-2026-05-07-module-creator-brainstorm.md (D2 — 20 typów MVP)
 * @see lib/custom-modules/ddl.ts (backend whitelist VALID_FIELD_TYPES)
 */

import type { ReactNode } from 'react';

import type { DictionaryParamBindingsMap } from '@/hooks/useDictionary';
import { type FieldRuleError, validateFieldRules } from '@/lib/field-rule-validation';

export type { FieldRuleError };

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FieldOption {
  value: string;
  label: string;
  color?: string;
}

/**
 * Reprezentacja pola modułu na frontendzie (uproszczona wersja schematu backendu).
 *
 * Mapowanie na backend:
 *  - `id`/`slug`/`name`/`fieldType` etc. → `custom_module_fields` (snake_case w DB, camelCase w API)
 *  - `options` → `custom_module_field_options` (D9 normalizacja, wczytane razem z polem)
 *  - `validationRules` → `custom_module_field_validation_rules` (key-value)
 */
export interface ModuleField {
  id: string;
  moduleId: string;
  name: string;
  slug: string;
  description: string | null;
  fieldType: string;
  isRequired: boolean;
  isUnique: boolean;
  isSearchable: boolean;
  isFilterable: boolean;
  showInList: boolean;
  defaultValue: string | null;
  /** Opcje dla select/multiselect/radio (z `custom_module_field_options`). */
  options?: FieldOption[];
  /**
   * Kod słownika globalnego (`dictionaries.code`, katalog ITE) źródła opcji dla pola
   * `select` (Etap 6c). Gdy obecny — opcje w runtime pochodzą ze słownika (przez
   * `useDictionary`) zamiast z `options`. NULL/undefined = zachowanie bez zmian
   * (opcje wpisane ręcznie przy tworzeniu modułu).
   */
  dictionaryCode?: string | null;
  /**
   * Nadpisanie sposobu wyświetlania pozycji słownika dla TEGO pola (Etap 1c) —
   * `null`/`undefined` = użyj `dictionary.displayMode` (fallback). Ma sens tylko
   * gdy `dictionaryCode` jest ustawiony.
   */
  dictionaryDisplayMode?: 'label' | 'code' | 'code_label' | null;
  /**
   * Wiązania parametrów słownika zewnętrznego (Etap 10.2/10.3, ADR-0066 §11;
   * Faza 2b ADR-0068 — tagowane wiązania lub legacy string = record_field) —
   * klucz = nazwa parametru słownika. Obecne i niepuste = opcje pobierane
   * dynamicznie (`useDictionaryOptions` z `bindings`+`recordValues`) zamiast
   * statycznie (`useDictionary`). NULL/undefined/puste = zachowanie bez zmian.
   */
  dictionaryParamBindings?: DictionaryParamBindingsMap | null;
  /** Reguły walidacji jako key-value (z `custom_module_field_validation_rules`). */
  validationRules?: Record<string, string>;
  /** Dla `relation` — id powiązanego modułu custom. */
  relationModuleId?: string | null;
  /**
   * Dla `relation` — slug kartoteki (encji systemowej: pracownicy/kontrahenci/użytkownicy),
   * gdy pole odwołuje się do kartoteki zamiast do modułu custom. Wzajemnie wykluczające się
   * z `relationModuleId` (kartoteki mają slug, nie UUID modułu). Ustawiane przez instalację
   * szablonu (część B mechaniki referencji). Patrz `lib/system-entity-adapters.ts`.
   */
  relationSystemModuleSlug?: string | null;
  /**
   * Pochodzenie pola (Krok 6). Wartość = pole z szablonu kreatora → chronione: klient nie
   * usuwa i nie przemianowuje (UI blokuje, backend egzekwuje). NULL = pole dodane ręcznie.
   */
  sourceTemplateModuleId?: string | null;
  /** Dla `relation` — slug pola wyświetlanego (np. record_name). */
  relationDisplayField?: string | null;
  /** Dla `relation` — czy multi-select rekordów. */
  relationMultiple?: boolean;
  /** T12.D.5 — slug pola-rodzica (filtrowanie opcji zależne). */
  dependsOnFieldSlug?: string | null;
  /** T12.D.5 — mapa parent_value → dozwolone wartości child. */
  dependsOnOptionMap?: Record<string, string[]> | null;
  /** T12.D.6 — cascade defaults z pola relation: { target_field_slug: local_field_slug }. */
  cascadeFromMap?: Record<string, string> | null;
}

export interface FieldInputProps {
  field: ModuleField;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  /** Auto-focus pierwszego inputu w formularzu. */
  autoFocus?: boolean;
  /**
   * Bieżące wartości wszystkich pól formularza (używane przez `formula`).
   * Klucz = slug pola, wartość = aktualna wartość.
   */
  formData?: Record<string, unknown>;
  /**
   * Lista wszystkich pól modułu (używane przez `formula` — potrzebuje typów pól).
   */
  fields?: ModuleField[];
  /**
   * ID aktualnego rekordu (używane przez `lookup` / `rollup` do fetch z API).
   * null gdy tworzony jest nowy rekord.
   */
  recordId?: string | null;
}

export interface FieldTypeDefinition {
  type: string;
  renderInput: (props: FieldInputProps) => ReactNode;
  /** Render wartości w komórce tabeli (ModuleRecordsPage list view). */
  renderCell: (field: ModuleField, value: unknown) => ReactNode;
  /** Walidacja wartości; zwraca error message lub null jeśli OK. */
  validate?: (value: unknown, field: ModuleField) => string | null;
  /** Domyślna wartość przy create (gdy `field.defaultValue` jest null). */
  defaultValue?: unknown;
}

// ─── Registry singleton ─────────────────────────────────────────────────────

class FieldRegistryClass {
  private types = new Map<string, FieldTypeDefinition>();

  register(definition: FieldTypeDefinition): void {
    this.types.set(definition.type, definition);
  }

  get(type: string): FieldTypeDefinition | undefined {
    return this.types.get(type);
  }

  has(type: string): boolean {
    return this.types.has(type);
  }

  getAll(): string[] {
    return Array.from(this.types.keys());
  }

  /** Reset rejestru — używane TYLKO w testach. */
  _reset(): void {
    this.types.clear();
  }

  renderInput(props: FieldInputProps): ReactNode {
    const def = this.get(props.field.fieldType);
    if (!def) {
      return this.renderUnknownInput(props);
    }
    return def.renderInput(props);
  }

  renderCell(field: ModuleField, value: unknown): ReactNode {
    const def = this.get(field.fieldType);
    if (!def) {
      return this.renderUnknownCell(value);
    }
    return def.renderCell(field, value);
  }

  /**
   * Walidacja wartości pola. Zwraca error message lub null.
   * Sprawdza:
   *  1. isRequired — wartość obecna i nie pusta
   *  2. type-specific validate (jeśli zarejestrowane)
   *  3. validationRules (min/max/pattern itd.) — TODO w T12.A12 (na MVP delegujemy do backendu)
   */
  validate(value: unknown, field: ModuleField): string | null {
    if (field.isRequired && this.isEmpty(value)) {
      return 'required';
    }
    const def = this.get(field.fieldType);
    if (def?.validate) {
      return def.validate(value, field);
    }
    return null;
  }

  /**
   * Egzekwuje reguły z kreatora (`field.validationRules`), sterowane typem pola.
   * Cienki adapter na WSPÓLNY walidator `validateFieldRules` (`lib/field-rule-validation`)
   * — ta sama logika i te same komunikaty co w kartotekach/dokumentach. Świadomie
   * odseparowane od `validate()` (kontrakt `string|null`), bo niesie parametry.
   */
  validateRules(value: unknown, field: ModuleField): FieldRuleError | null {
    return validateFieldRules(field.fieldType, field.validationRules, value);
  }

  defaultValue(field: ModuleField): unknown {
    if (field.defaultValue !== null && field.defaultValue !== undefined) {
      // Backend trzyma defaultValue jako TEXT — type coerce per typ.
      return field.defaultValue;
    }
    const def = this.get(field.fieldType);
    return def?.defaultValue ?? null;
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  private isEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  }

  private renderUnknownInput(props: FieldInputProps): ReactNode {
    return (
      <input
        type="text"
        value={typeof props.value === 'string' ? props.value : ''}
        onChange={(e) => props.onChange(e.target.value)}
        disabled={props.disabled}
        className="w-full rounded-md border border-border-primary px-3 py-2 text-sm"
        data-testid={`field-${props.field.slug}-unknown`}
      />
    );
  }

  private renderUnknownCell(value: unknown): ReactNode {
    if (value === null || value === undefined) return null;
    return String(value);
  }
}

export const FieldRegistry = new FieldRegistryClass();
